import { defineStore } from 'pinia';
import { computed, getCurrentInstance, onMounted, ref, watch } from 'vue';
import { useAuthStore } from '@/stores/authStore';

export const UI_VERSIONS = {
  V1: 'v1',
  V2: 'v2'
};

export const useUiVersion = defineStore(
  'uiVersionStore',
  () => {
    const uiVersion = ref(UI_VERSIONS.V1);
    const authStore = useAuthStore();

    // Partner-referred users (ref from /me) always get v2 and cannot switch back.
    const userRef = computed(() => authStore.user?.ref ?? null);

    const hasPartnerRef = computed(() => userRef.value !== null);

    const isV2 = computed(() => hasPartnerRef.value || uiVersion.value === UI_VERSIONS.V2);

    const isV1Locked = computed(() => hasPartnerRef.value);

    const applyClass = () => {
      if (isV2.value) {
        document.documentElement.classList.add('app-v2');
      } else {
        document.documentElement.classList.remove('app-v2');
      }
    };

    const setVersion = (version) => {
      if (hasPartnerRef.value) {
        uiVersion.value = UI_VERSIONS.V2;
        applyClass();
        return;
      }
      uiVersion.value = version === UI_VERSIONS.V2 ? UI_VERSIONS.V2 : UI_VERSIONS.V1;
      applyClass();
    };

    const toggleVersion = () => {
      if (hasPartnerRef.value) {
        return;
      }
      setVersion(uiVersion.value === UI_VERSIONS.V2 ? UI_VERSIONS.V1 : UI_VERSIONS.V2);
    };

    watch(isV2, () => {
      applyClass();
    });

    watch(userRef, () => {
      if (hasPartnerRef.value) {
        uiVersion.value = UI_VERSIONS.V2;
      }
      applyClass();
    });

    if (getCurrentInstance()) {
      onMounted(() => {
        applyClass();
      });
    } else {
      applyClass();
    }

    return {
      uiVersion,
      userRef,
      hasPartnerRef,
      isV2,
      isV1Locked,
      setVersion,
      toggleVersion
    };
  },
  { persist: true }
);
